/**
 * ImageTreatmentsTab — Phase 2 admin preview tool.
 *
 * Lets the operator pick a treatment profile (resiligent_institutional /
 * remi_warm / conceptual_mono / custom workspace overrides), pick a real
 * brand_asset OR upload an ad-hoc photo, and see before/after side-by-side.
 *
 * Slider controls intensity (0-1, default 1.0). Profile parameters are
 * shown in a JSON panel — directly editable so the operator can tune
 * desat/contrast/warmth/grain against a live preview before saving the
 * override via PUT /brand/image-treatments/{key}.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Skeleton } from "../ui/skeleton";
import { Badge } from "../ui/badge";
import { Slider } from "../ui/slider";
import { Loader2, Upload, RotateCw, Save, ImageOff, Wand2 } from "lucide-react";
import { toast } from "sonner";

const _API_BASE =
  typeof window !== "undefined" && window.location.hostname !== "localhost"
    ? window.location.origin
    : process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const API = `${_API_BASE}/api`;

function authedFetch(url, opts = {}) {
  return fetch(url, { credentials: "include", ...opts });
}

export default function ImageTreatmentsTab({ workspaceId }) {
  const [profiles, setProfiles] = useState([]);
  const [activeKey, setActiveKey] = useState("resiligent_institutional");
  const [intensity, setIntensity] = useState(1.0);
  const [assets, setAssets] = useState([]);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadedPreviewUrl, setUploadedPreviewUrl] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [profileJsonText, setProfileJsonText] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const fileInputRef = useRef(null);

  const activeProfile = useMemo(
    () => profiles.find((p) => p.key === activeKey) || null,
    [profiles, activeKey],
  );

  // ── Load profiles + a small slice of active assets ──────────────────
  const loadProfiles = useCallback(async () => {
    try {
      const r = await authedFetch(`${API}/v2/workspaces/${workspaceId}/brand/image-treatments`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();
      setProfiles(json.profiles || []);
    } catch (e) {
      toast.error(`Could not load profiles: ${e.message}`);
    }
  }, [workspaceId]);

  const loadAssets = useCallback(async () => {
    try {
      const r = await authedFetch(
        `${API}/v2/workspaces/${workspaceId}/brand/gallery?is_active=true&per_page=24&classifier_verdict=safe`,
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();
      setAssets(json.items || []);
      if ((json.items || []).length > 0 && !selectedAssetId) {
        setSelectedAssetId(json.items[0].asset_id);
      }
    } catch (e) {
      toast.error(`Could not load assets: ${e.message}`);
    }
  }, [workspaceId, selectedAssetId]);

  useEffect(() => { loadProfiles(); loadAssets(); }, [loadProfiles, loadAssets]);

  // Keep the JSON-editor in sync with the selected profile.
  useEffect(() => {
    if (activeProfile) {
      setProfileJsonText(JSON.stringify(activeProfile, null, 2));
    }
  }, [activeProfile]);

  // ── Trigger the preview on profile/asset/intensity change ───────────
  const fetchPreview = useCallback(async () => {
    if (!activeKey) return;
    if (!selectedAssetId && !uploadedFile) return;
    setLoadingPreview(true);
    try {
      let resp;
      if (uploadedFile) {
        const fd = new FormData();
        fd.append("file", uploadedFile);
        fd.append("profile_key", activeKey);
        fd.append("intensity", String(intensity));
        resp = await authedFetch(
          `${API}/v2/workspaces/${workspaceId}/brand/image-treatments/preview-upload`,
          { method: "POST", body: fd },
        );
      } else {
        resp = await authedFetch(
          `${API}/v2/workspaces/${workspaceId}/brand/image-treatments/preview`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              asset_id: selectedAssetId,
              profile_key: activeKey,
              intensity: Number(intensity),
            }),
          },
        );
      }
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      setPreviewUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch (e) {
      toast.error(`Preview failed: ${e.message}`);
    } finally {
      setLoadingPreview(false);
    }
  }, [activeKey, selectedAssetId, uploadedFile, intensity, workspaceId]);

  // Debounce: refire preview when key/asset/intensity changes (300ms idle)
  useEffect(() => {
    const t = setTimeout(fetchPreview, 300);
    return () => clearTimeout(t);
  }, [fetchPreview]);

  // ── Upload handler ──────────────────────────────────────────────────
  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadedFile(f);
    setSelectedAssetId(null);
    if (uploadedPreviewUrl) URL.revokeObjectURL(uploadedPreviewUrl);
    setUploadedPreviewUrl(URL.createObjectURL(f));
  };

  const clearUpload = () => {
    setUploadedFile(null);
    if (uploadedPreviewUrl) URL.revokeObjectURL(uploadedPreviewUrl);
    setUploadedPreviewUrl(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ── Save profile override ───────────────────────────────────────────
  const saveProfileOverride = async () => {
    let profile;
    try {
      profile = JSON.parse(profileJsonText);
    } catch (e) {
      toast.error("Profile JSON is invalid — fix the syntax first");
      return;
    }
    setSavingProfile(true);
    try {
      const r = await authedFetch(
        `${API}/v2/workspaces/${workspaceId}/brand/image-treatments/${activeKey}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ profile_key: activeKey, profile }),
        },
      );
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`HTTP ${r.status}: ${t.slice(0, 200)}`);
      }
      toast.success(`Saved override for ${activeKey}`);
      await loadProfiles();
    } catch (e) {
      toast.error(`Save failed: ${e.message}`);
    } finally {
      setSavingProfile(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div className="space-y-6" data-testid="image-treatments-tab">
      {/* Header & profile picker */}
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-violet-600" /> Image treatments
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Brand grading and duotone profiles applied to every photo before the compositor places it. Pick a profile, pick a test photo, and tune until it looks right.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={loadProfiles} data-testid="treatments-refresh">
          <RotateCw className="h-3.5 w-3.5 mr-1" /> Refresh
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap" data-testid="treatments-profile-picker">
        {profiles.map((p) => (
          <button
            key={p.key}
            type="button"
            onClick={() => setActiveKey(p.key)}
            className={`px-3 py-2 rounded-lg border text-sm transition ${
              p.key === activeKey
                ? "bg-violet-600 text-white border-violet-600"
                : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"
            }`}
            data-testid={`treatments-profile-${p.key}`}
          >
            {p.label || p.key}
          </button>
        ))}
      </div>

      {/* Intensity slider */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-xs font-medium text-slate-700 min-w-[120px]">
            Intensity: <span className="font-mono">{intensity.toFixed(2)}</span>
          </label>
          <div className="flex-1 min-w-[200px] max-w-md">
            <Slider
              value={[intensity]}
              onValueChange={(v) => setIntensity(v[0] ?? 1.0)}
              min={0}
              max={1}
              step={0.05}
              data-testid="treatments-intensity-slider"
            />
            <div className="flex justify-between text-[10px] text-slate-400 mt-1">
              <span>0 passthrough</span>
              <span>0.5</span>
              <span>1.0 full</span>
            </div>
          </div>
        </div>
      </div>

      {/* Test photo picker — assets row + upload */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="text-sm font-medium text-slate-800">Test photo</h3>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
              data-testid="treatments-upload-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              data-testid="treatments-upload-button"
            >
              <Upload className="h-3.5 w-3.5 mr-1" /> Upload test image
            </Button>
            {uploadedFile ? (
              <Button variant="ghost" size="sm" onClick={clearUpload} data-testid="treatments-clear-upload">
                Clear upload
              </Button>
            ) : null}
          </div>
        </div>
        {!uploadedFile ? (
          <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2" data-testid="treatments-asset-grid">
            {assets.map((a) => (
              <button
                key={a.asset_id}
                type="button"
                onClick={() => { setSelectedAssetId(a.asset_id); }}
                className={`aspect-square rounded border-2 overflow-hidden ${
                  a.asset_id === selectedAssetId
                    ? "border-violet-500 ring-2 ring-violet-200"
                    : "border-slate-200 hover:border-slate-300"
                }`}
                data-testid={`treatments-asset-${a.asset_id}`}
              >
                {a.thumbnail_url ? (
                  <img src={a.thumbnail_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-slate-400">
                    <ImageOff className="h-6 w-6" />
                  </div>
                )}
              </button>
            ))}
          </div>
        ) : (
          <div className="text-xs text-slate-600 bg-slate-50 px-3 py-2 rounded">
            Using uploaded file: <span className="font-mono">{uploadedFile.name}</span> ({Math.round(uploadedFile.size / 1024)} KB)
          </div>
        )}
      </div>

      {/* Before / after preview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-3" data-testid="treatments-before">
          <div className="text-xs font-medium text-slate-600 mb-2">Before (original)</div>
          {uploadedPreviewUrl ? (
            <img src={uploadedPreviewUrl} alt="Original" className="w-full rounded" />
          ) : selectedAssetId ? (
            <img
              src={`${API}/v2/workspaces/${workspaceId}/brand/assets/${selectedAssetId}/full`}
              alt="Original"
              className="w-full rounded"
              crossOrigin="use-credentials"
            />
          ) : (
            <Skeleton className="w-full h-64 rounded" />
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-3 relative" data-testid="treatments-after">
          <div className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-2">
            After ({activeKey} @ {intensity.toFixed(2)})
            {loadingPreview ? <Loader2 className="h-3 w-3 animate-spin text-violet-600" /> : null}
          </div>
          {previewUrl ? (
            <img src={previewUrl} alt="Treated" className="w-full rounded" />
          ) : (
            <Skeleton className="w-full h-64 rounded" />
          )}
        </div>
      </div>

      {/* Profile JSON editor */}
      {activeProfile ? (
        <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3" data-testid="treatments-profile-editor">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-sm font-medium text-slate-800">
                Profile config — {activeProfile.label || activeKey}
              </h3>
              <p className="text-[11px] text-slate-500">
                Edit the JSON to tune parameters live. Preview re-renders on every change. Save to persist as a workspace override.
              </p>
            </div>
            <Button
              size="sm"
              onClick={saveProfileOverride}
              disabled={savingProfile}
              data-testid="treatments-save-profile"
            >
              {savingProfile ? (
                <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Saving…</>
              ) : (
                <><Save className="h-3.5 w-3.5 mr-1" /> Save override</>
              )}
            </Button>
          </div>
          <Textarea
            value={profileJsonText}
            onChange={(e) => setProfileJsonText(e.target.value)}
            className="font-mono text-xs"
            rows={12}
            data-testid="treatments-profile-json"
          />
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <Badge variant="outline" className="text-[10px]">method: {activeProfile.method}</Badge>
            <span>•</span>
            <span>Live preview updates 300ms after edits stop.</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
